import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import {
  getQuotaDefinitions,
  updateQuotaDefinition,
  getUsersWithOverrides,
  getUserQuotas,
  setUserOverride,
  removeUserOverride,
  seedQuotaDefinitions,
} from '../../api/quotas';
import DWLFLoader from '../../components/DWLFLoader';

const QUOTA_TYPE_LABELS = {
  backtest: 'Backtests',
  aiAgent: 'AI Agent',
  evaluation: 'Evaluations',
  signal: 'Signals',
};

const QUOTA_TYPE_DESCRIPTIONS = {
  backtest: 'Strategy backtesting requests',
  aiAgent: 'AI-powered analysis and chat',
  evaluation: 'Strategy evaluation runs',
  signal: 'Trade signal generation',
};

const formatLimit = (value) => {
  if (value === null || value === undefined) return 'Unlimited';
  return value.toLocaleString();
};

const QuotaDefinitionsSection = ({ definitions, loading, onRefresh, onEdit, onSeed }) => {
  // definitions is an array of { quotaType, label, description, definitions: { roleId: limits } }
  // Transform into grouped format for display

  return (
    <Card
      sx={{
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        background: 'linear-gradient(145deg, var(--dwlf-ink-02), rgba(0,0,0,0.25))',
        mb: 3,
      }}
    >
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h6" gutterBottom>
              Quota Definitions
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Default quota limits by role. These apply to all users unless overridden.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Seed default definitions">
              <Button variant="outlined" size="small" onClick={onSeed} disabled={loading}>
                Seed Defaults
              </Button>
            </Tooltip>
            <Tooltip title="Refresh">
              <IconButton onClick={onRefresh} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <DWLFLoader size="medium" showText={false} />
          </Box>
        ) : definitions.length === 0 ? (
          <Alert severity="info">
            No quota definitions found. Click "Seed Defaults" to create the default quota limits.
          </Alert>
        ) : (
          <Stack spacing={2}>
            {definitions.map((quotaDef) => (
              <Accordion key={quotaDef.quotaType} defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {quotaDef.label || QUOTA_TYPE_LABELS[quotaDef.quotaType] || quotaDef.quotaType}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {quotaDef.description || QUOTA_TYPE_DESCRIPTIONS[quotaDef.quotaType] || ''}
                    </Typography>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer component={Paper} variant="outlined" sx={{ backgroundColor: 'transparent' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Role</TableCell>
                          <TableCell align="center">Daily Limit</TableCell>
                          <TableCell align="center">Monthly Limit</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Object.entries(quotaDef.definitions || {}).map(([roleId, limits]) => (
                          <TableRow key={`${quotaDef.quotaType}-${roleId}`} hover>
                            <TableCell>
                              <Chip
                                label={roleId}
                                size="small"
                                color={roleId === 'admin' ? 'secondary' : 'primary'}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell align="center">
                              {limits?.daily === null ? (
                                <Chip icon={<AllInclusiveIcon />} label="Unlimited" size="small" color="success" />
                              ) : (
                                formatLimit(limits?.daily)
                              )}
                            </TableCell>
                            <TableCell align="center">
                              {limits?.monthly === null ? (
                                <Chip icon={<AllInclusiveIcon />} label="Unlimited" size="small" color="success" />
                              ) : (
                                formatLimit(limits?.monthly)
                              )}
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title="Edit limits">
                                <IconButton
                                  size="small"
                                  onClick={() => onEdit({
                                    quotaType: quotaDef.quotaType,
                                    roleId,
                                    limits,
                                  })}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

const UserQuotasSection = ({
  usersWithOverrides,
  loading,
  onRefresh,
  onSearchUser,
  onEditOverride,
  onRemoveOverride,
  searchedUser,
  searchLoading,
}) => {
  const [searchInput, setSearchInput] = useState('');

  const handleSearch = () => {
    if (searchInput.trim()) {
      onSearchUser(searchInput.trim());
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !searchLoading) {
      handleSearch();
    }
  };

  return (
    <Card
      sx={{
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        background: 'linear-gradient(145deg, var(--dwlf-ink-02), rgba(0,0,0,0.25))',
      }}
    >
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h6" gutterBottom>
              User Quota Overrides
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Set custom quota limits for individual users.
            </Typography>
          </Box>
          <Tooltip title="Refresh">
            <IconButton onClick={onRefresh} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>

        <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
          <TextField
            placeholder="Search by Account ID or Email"
            size="small"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyPress={handleKeyPress}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ flexGrow: 1, maxWidth: 400 }}
          />
          <Button variant="contained" onClick={handleSearch} disabled={searchLoading || !searchInput.trim()}>
            {searchLoading ? <DWLFLoader size="tiny" showText={false} /> : 'Search'}
          </Button>
        </Stack>

        {searchedUser && (
          <Paper sx={{ p: 2, mb: 3, backgroundColor: 'background.default' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>
                  {searchedUser.email || searchedUser.accountId}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    Role:
                  </Typography>
                  <Chip label={searchedUser.role || 'user'} size="small" color="primary" variant="outlined" />
                </Stack>
              </Box>
            </Stack>
            <Divider sx={{ my: 2 }} />
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Quota Type</TableCell>
                    <TableCell align="center">Daily Used</TableCell>
                    <TableCell align="center">Daily Limit</TableCell>
                    <TableCell align="center">Monthly Used</TableCell>
                    <TableCell align="center">Monthly Limit</TableCell>
                    <TableCell align="center">Override</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(searchedUser.quotas || {}).map(([quotaType, data]) => (
                    <TableRow key={quotaType} hover>
                      <TableCell>
                        <Typography fontWeight={500}>
                          {QUOTA_TYPE_LABELS[quotaType] || quotaType}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">{data.usage?.daily || 0}</TableCell>
                      <TableCell align="center">
                        {data.limits?.daily === null ? (
                          <AllInclusiveIcon fontSize="small" color="success" />
                        ) : (
                          data.limits?.daily ?? '-'
                        )}
                      </TableCell>
                      <TableCell align="center">{data.usage?.monthly || 0}</TableCell>
                      <TableCell align="center">
                        {data.limits?.monthly === null ? (
                          <AllInclusiveIcon fontSize="small" color="success" />
                        ) : (
                          data.limits?.monthly ?? '-'
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {data.hasOverride ? (
                          <Chip label="Custom" size="small" color="secondary" />
                        ) : (
                          <Chip label="Default" size="small" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title="Set override">
                            <IconButton
                              size="small"
                              onClick={() => onEditOverride(searchedUser.accountId, quotaType, data)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {data.hasOverride && (
                            <Tooltip title="Remove override">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => onRemoveOverride(searchedUser.accountId, quotaType)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Users with Active Overrides
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <DWLFLoader size="medium" showText={false} />
          </Box>
        ) : usersWithOverrides.length === 0 ? (
          <Alert severity="info">No users have custom quota overrides.</Alert>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ backgroundColor: 'transparent' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Quota Type</TableCell>
                  <TableCell align="center">Daily Limit</TableCell>
                  <TableCell align="center">Monthly Limit</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Expires</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {usersWithOverrides.map((user) => (
                  <TableRow key={`${user.accountId}-${user.quotaType}`} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {user.email || user.accountId}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={QUOTA_TYPE_LABELS[user.quotaType] || user.quotaType}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center">
                      {user.override?.limits?.daily === null ? (
                        <AllInclusiveIcon fontSize="small" color="success" />
                      ) : (
                        user.override?.limits?.daily ?? '-'
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {user.override?.limits?.monthly === null ? (
                        <AllInclusiveIcon fontSize="small" color="success" />
                      ) : (
                        user.override?.limits?.monthly ?? '-'
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 200 }} noWrap>
                        {user.override?.reason || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {user.override?.expiresAt ? (
                        <Typography variant="body2">
                          {new Date(user.override.expiresAt).toLocaleDateString()}
                        </Typography>
                      ) : (
                        <Chip label="Never" size="small" />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="Edit override">
                          <IconButton
                            size="small"
                            onClick={() => onEditOverride(user.accountId, user.quotaType, user)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove override">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => onRemoveOverride(user.accountId, user.quotaType)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
};

const QuotaManagement = () => {
  const [definitions, setDefinitions] = useState([]);
  const [usersWithOverrides, setUsersWithOverrides] = useState([]);
  const [loading, setLoading] = useState(false);
  const [overridesLoading, setOverridesLoading] = useState(false);
  const [error, setError] = useState('');

  // Search state
  const [searchedUser, setSearchedUser] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);

  // Definition edit dialog
  const [defDialogOpen, setDefDialogOpen] = useState(false);
  const [editingDef, setEditingDef] = useState(null);
  const [defDailyLimit, setDefDailyLimit] = useState('');
  const [defMonthlyLimit, setDefMonthlyLimit] = useState('');
  const [defDailyUnlimited, setDefDailyUnlimited] = useState(false);
  const [defMonthlyUnlimited, setDefMonthlyUnlimited] = useState(false);
  const [defSaving, setDefSaving] = useState(false);
  const [defError, setDefError] = useState('');

  // Override edit dialog
  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);
  const [editingOverride, setEditingOverride] = useState(null);
  const [overrideDailyLimit, setOverrideDailyLimit] = useState('');
  const [overrideMonthlyLimit, setOverrideMonthlyLimit] = useState('');
  const [overrideDailyUnlimited, setOverrideDailyUnlimited] = useState(false);
  const [overrideMonthlyUnlimited, setOverrideMonthlyUnlimited] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [overrideExpiresAt, setOverrideExpiresAt] = useState('');
  const [overrideSaving, setOverrideSaving] = useState(false);
  const [overrideError, setOverrideError] = useState('');

  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingOverride, setDeletingOverride] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const loadDefinitions = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getQuotaDefinitions();
      setDefinitions(data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load quota definitions');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadUsersWithOverrides = useCallback(async () => {
    setOverridesLoading(true);
    try {
      const data = await getUsersWithOverrides();
      setUsersWithOverrides(data || []);
    } catch (err) {
      console.error('Failed to load users with overrides:', err);
      setError(err.response?.data?.message || 'Failed to load users with overrides');
    } finally {
      setOverridesLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDefinitions();
    loadUsersWithOverrides();
  }, [loadDefinitions, loadUsersWithOverrides]);

  const handleSeedDefinitions = async () => {
    setLoading(true);
    try {
      await seedQuotaDefinitions();
      await loadDefinitions();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to seed quota definitions');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchUser = async (query) => {
    setError('');
    setSearchLoading(true);
    setSearchedUser(null);
    try {
      const data = await getUserQuotas(query);
      setSearchedUser(data);
    } catch (err) {
      setError(err.response?.data?.message || 'User not found');
    } finally {
      setSearchLoading(false);
    }
  };

  // Definition edit handlers
  const openDefDialog = (def) => {
    setEditingDef(def);
    setDefDailyLimit(def.limits?.daily ?? '');
    setDefMonthlyLimit(def.limits?.monthly ?? '');
    setDefDailyUnlimited(def.limits?.daily === null);
    setDefMonthlyUnlimited(def.limits?.monthly === null);
    setDefError('');
    setDefDialogOpen(true);
  };

  const closeDefDialog = () => {
    setDefDialogOpen(false);
    setEditingDef(null);
  };

  const handleSaveDefinition = async () => {
    setDefSaving(true);
    setDefError('');
    try {
      const limits = {
        daily: defDailyUnlimited ? null : parseInt(defDailyLimit, 10) || 0,
        monthly: defMonthlyUnlimited ? null : parseInt(defMonthlyLimit, 10) || 0,
      };
      await updateQuotaDefinition(editingDef.quotaType, editingDef.roleId, limits);
      closeDefDialog();
      await loadDefinitions();
    } catch (err) {
      setDefError(err.response?.data?.message || 'Failed to update definition');
    } finally {
      setDefSaving(false);
    }
  };

  // Override edit handlers
  const openOverrideDialog = (accountId, quotaType, data) => {
    // Handle two different data structures:
    // - From searched user: { limits, usage, hasOverride } (no override object)
    // - From users-with-overrides: { override: { limits, reason, expiresAt }, ... }
    const override = data.override || {};
    const limits = override.limits || data.limits || {};
    
    setEditingOverride({ accountId, quotaType, data });
    setOverrideDailyLimit(limits.daily ?? '');
    setOverrideMonthlyLimit(limits.monthly ?? '');
    setOverrideDailyUnlimited(limits.daily === null);
    setOverrideMonthlyUnlimited(limits.monthly === null);
    setOverrideReason(override.reason || '');
    setOverrideExpiresAt(override.expiresAt ? override.expiresAt.split('T')[0] : '');
    setOverrideError('');
    setOverrideDialogOpen(true);
  };

  const closeOverrideDialog = () => {
    setOverrideDialogOpen(false);
    setEditingOverride(null);
  };

  const handleSaveOverride = async () => {
    setOverrideSaving(true);
    setOverrideError('');
    try {
      const override = {
        daily: overrideDailyUnlimited ? null : parseInt(overrideDailyLimit, 10) || 0,
        monthly: overrideMonthlyUnlimited ? null : parseInt(overrideMonthlyLimit, 10) || 0,
        reason: overrideReason || undefined,
        expiresAt: overrideExpiresAt || undefined,
      };
      await setUserOverride(editingOverride.accountId, editingOverride.quotaType, override);
      closeOverrideDialog();
      await loadUsersWithOverrides();
      if (searchedUser && searchedUser.accountId === editingOverride.accountId) {
        await handleSearchUser(editingOverride.accountId);
      }
    } catch (err) {
      setOverrideError(err.response?.data?.message || 'Failed to set override');
    } finally {
      setOverrideSaving(false);
    }
  };

  // Delete override handlers
  const openDeleteDialog = (accountId, quotaType) => {
    setDeletingOverride({ accountId, quotaType });
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setDeletingOverride(null);
  };

  const handleDeleteOverride = async () => {
    if (!deletingOverride) return;
    setDeleteLoading(true);
    try {
      await removeUserOverride(deletingOverride.accountId, deletingOverride.quotaType);
      closeDeleteDialog();
      await loadUsersWithOverrides();
      if (searchedUser && searchedUser.accountId === deletingOverride.accountId) {
        await handleSearchUser(deletingOverride.accountId);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to remove override');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <QuotaDefinitionsSection
        definitions={definitions}
        loading={loading}
        onRefresh={loadDefinitions}
        onEdit={openDefDialog}
        onSeed={handleSeedDefinitions}
      />

      <UserQuotasSection
        usersWithOverrides={usersWithOverrides}
        loading={overridesLoading}
        onRefresh={loadUsersWithOverrides}
        onSearchUser={handleSearchUser}
        onEditOverride={openOverrideDialog}
        onRemoveOverride={openDeleteDialog}
        searchedUser={searchedUser}
        searchLoading={searchLoading}
      />

      {/* Edit Definition Dialog */}
      <Dialog open={defDialogOpen} onClose={closeDefDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Edit Quota Definition: {QUOTA_TYPE_LABELS[editingDef?.quotaType]} ({editingDef?.roleId})
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {defError && <Alert severity="error">{defError}</Alert>}

            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={defDailyUnlimited}
                    onChange={(e) => setDefDailyUnlimited(e.target.checked)}
                  />
                }
                label="Unlimited daily"
              />
              {!defDailyUnlimited && (
                <TextField
                  label="Daily Limit"
                  type="number"
                  value={defDailyLimit}
                  onChange={(e) => setDefDailyLimit(e.target.value)}
                  fullWidth
                  size="small"
                  sx={{ mt: 1 }}
                  inputProps={{ min: 0 }}
                />
              )}
            </Box>

            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={defMonthlyUnlimited}
                    onChange={(e) => setDefMonthlyUnlimited(e.target.checked)}
                  />
                }
                label="Unlimited monthly"
              />
              {!defMonthlyUnlimited && (
                <TextField
                  label="Monthly Limit"
                  type="number"
                  value={defMonthlyLimit}
                  onChange={(e) => setDefMonthlyLimit(e.target.value)}
                  fullWidth
                  size="small"
                  sx={{ mt: 1 }}
                  inputProps={{ min: 0 }}
                />
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDefDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveDefinition} disabled={defSaving}>
            {defSaving ? <DWLFLoader size="tiny" showText={false} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Override Dialog */}
      <Dialog open={overrideDialogOpen} onClose={closeOverrideDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Set Quota Override: {QUOTA_TYPE_LABELS[editingOverride?.quotaType]}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {overrideError && <Alert severity="error">{overrideError}</Alert>}

            <Typography variant="body2" color="text.secondary">
              Account: {editingOverride?.accountId}
            </Typography>

            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={overrideDailyUnlimited}
                    onChange={(e) => setOverrideDailyUnlimited(e.target.checked)}
                  />
                }
                label="Unlimited daily"
              />
              {!overrideDailyUnlimited && (
                <TextField
                  label="Daily Limit"
                  type="number"
                  value={overrideDailyLimit}
                  onChange={(e) => setOverrideDailyLimit(e.target.value)}
                  fullWidth
                  size="small"
                  sx={{ mt: 1 }}
                  inputProps={{ min: 0 }}
                />
              )}
            </Box>

            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={overrideMonthlyUnlimited}
                    onChange={(e) => setOverrideMonthlyUnlimited(e.target.checked)}
                  />
                }
                label="Unlimited monthly"
              />
              {!overrideMonthlyUnlimited && (
                <TextField
                  label="Monthly Limit"
                  type="number"
                  value={overrideMonthlyLimit}
                  onChange={(e) => setOverrideMonthlyLimit(e.target.value)}
                  fullWidth
                  size="small"
                  sx={{ mt: 1 }}
                  inputProps={{ min: 0 }}
                />
              )}
            </Box>

            <TextField
              label="Reason (optional)"
              value={overrideReason}
              onChange={(e) => setOverrideReason(e.target.value)}
              fullWidth
              multiline
              rows={2}
              placeholder="Why is this override being set?"
            />

            <TextField
              label="Expires At (optional)"
              type="date"
              value={overrideExpiresAt}
              onChange={(e) => setOverrideExpiresAt(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText="Leave empty for permanent override"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeOverrideDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveOverride} disabled={overrideSaving}>
            {overrideSaving ? <DWLFLoader size="tiny" showText={false} /> : 'Save Override'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Override Confirmation */}
      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog}>
        <DialogTitle>Remove Quota Override</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove the {QUOTA_TYPE_LABELS[deletingOverride?.quotaType]} quota override
            for this user?
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            The user will revert to the default role-based quota limits.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteOverride} disabled={deleteLoading}>
            {deleteLoading ? <DWLFLoader size="tiny" showText={false} /> : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QuotaManagement;
